<script setup lang="ts">
import InlineLoader from '@/components/general/InlineLoader.vue';
import LoadingStatus from '@/assets/js/enum/LoadingStatus';
import { toRefs } from 'vue';

interface FinalButton {
  text: string;
  img?: string;
}

interface Props {
  activeStep: number;
  totalSteps: number;
  finalButton: FinalButton;
  finalProcessStatus: string;
  saveButton: boolean;
  disabelBtn: boolean;
  fieldValidation?: boolean;
  finalSaveAndClose?: boolean;
  citationFramingModalStep?: boolean;
}

interface Emits {
  (e: 'stepChange', step: number): void;
  (e: 'dataSave'): void;
  (e: 'handleSubmit'): void;
}

const props = withDefaults(defineProps<Props>(), {
  finalButton: () => ({ text: '' }),
  finalProcessStatus: LoadingStatus.NONE,
  saveButton: false,
  disabelBtn: false,
  fieldValidation: false,
  finalSaveAndClose: true,
  citationFramingModalStep: false,
});

const {
  activeStep,
  totalSteps,
  finalButton,
  finalProcessStatus,
  saveButton,
  disabelBtn,
  fieldValidation,
  finalSaveAndClose,
  citationFramingModalStep,
} = toRefs(props);

const emit = defineEmits<Emits>();
</script>

<template>
  <div class="uk-flex uk-flex-between" :class="{ 'final-step-align': activeStep === totalSteps }">
    <div class="uk-flex">
      <button
        :disabled="activeStep === 1 || citationFramingModalStep"
        class="uk-button uk-button-default uk-margin-top uk-border-rounded uk-padding-remove-left uk-flex uk-flex-middle"
        :class="{ 'invitee-back-btn': activeStep === totalSteps }"
        @click="emit('stepChange', activeStep - 1)"
      >
        <span uk-icon="icon:triangle-up;ratio:1.5" class="uk-margin-small-right" />
        <span>Back</span>
      </button>
      <button
        :disabled="(activeStep === totalSteps && !saveButton) || fieldValidation"
        :class="{ 'btn-none': activeStep === totalSteps && saveButton }"
        class="uk-button uk-button-default uk-button-primary uk-margin-top uk-border-rounded uk-margin-small-left uk-padding-remove-right uk-flex uk-flex-middle"
        data-tid="discussion-next"
        @click="emit('stepChange', activeStep + 1)"
      >
        <span>Next</span>
        <span uk-icon="icon:triangle-down;ratio:1.5" class="uk-margin-small-left" />
      </button>
    </div>
    <button
      v-if="activeStep === totalSteps && saveButton"
      class="uk-button uk-button-default uk-margin-top uk-border-rounded capitalize"
      @click="emit('dataSave')"
    >
      Save and Close
    </button>
    <button
      v-if="activeStep === totalSteps && finalSaveAndClose"
      class="uk-button uk-button-default uk-button-primary uk-margin-top uk-border-rounded invite-final-btn"
      :class="{
        'uk-flex': finalButton.img,
        'uk-flex-middle': finalButton.img,
      }"
      :disabled="disabelBtn"
      @click="emit('handleSubmit')"
    >
      <span>{{ finalButton.text }}</span>
      <InlineLoader
        v-if="finalProcessStatus !== LoadingStatus.NONE"
        :status="finalProcessStatus"
        class="uk-margin-small-left"
      />
      <img
        v-else-if="finalButton.img"
        :src="finalButton.img"
        style="width: 20px; height: 20px"
        class="uk-margin-small-left"
      />
    </button>
    <button
      v-else-if="finalSaveAndClose"
      @click="emit('dataSave')"
      class="uk-button uk-button-default uk-margin-top uk-border-rounded capitalize"
    >
      Save and Close
    </button>
  </div>
</template>

<style lang="scss">
.btn-none {
  display: none !important;
}

.invite-final-btn {
  height: 40px;
}

.invitee-back-btn {
  height: 40px;
}

.invite-alert-box {
  width: 26vw !important;
  margin-bottom: 0 !important;
}

.final-step-align {
  display: flex;
  align-items: flex-end;
}
</style>
